import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Icon from "@material-ui/core/Icon";
import FieldGroup from "../../interfaces/FieldGroup";
import FieldSelectionMap from "../../interfaces/FieldSelectionMap";
import {
  ClickAwayListener,
  Grow,
  Paper as UnstyledPaper,
  Popper as UnstyledPopper,
  List as UnstyledList,
} from "@material-ui/core";
import UnstyledButton from "../Button";
import FieldGroupOption from "./FieldGroupOption";
import SearchBar from "../SearchBar";
import FieldOption from "./FieldOption";
import { getUniqueFieldsFromGroupsBySearchQuery } from "../../ManageReport/utils";
import Field from "../../interfaces/Field";

const Container = styled.div`
  && {
    font-family: Inter, system-ui;
    font-weight: 400;
  }
`;

const Button = styled(UnstyledButton)`
  && {
    width: 280px;
    justify-content: space-between;

    &[aria-owns="field-list"] {
      background-color: rgba(0, 0, 0, 0.08);
    }

    span.material-icons {
      width: 25px;
      height: 25px;
      margin-right: -4px;
      margin-left: 8px;
    }
  }
`;

const Popper = styled(UnstyledPopper)`
  z-index: 1000;
`;

const Paper = styled(UnstyledPaper)`
  && {
    width: 280px;
    box-sizing: border-box;
    border: 1px solid rgba(0, 0, 0, 0.2);
    box-shadow: 0px 4px 5px 0px rgba(0, 0, 0, 0.04);
    border-radius: 8px;
    box-sizing: border-box;
    max-height: 40vh;
    overflow-y: auto;
  }
`;

const SearchBarContainer = styled.div`
  && {
    padding: 16px 16px 0 16px;
  }
`;

const ListContainer = styled.div`
  && {
    padding: 8px 16px;

    p {
      padding: 8px 0;
      text-align: center;
    }
  }
`;

const List = styled(UnstyledList)`
  && {
    & > li {
      padding: 8px 0;
      user-select: none;
    }
  }
`;

interface FieldMultiSelectProps {
  fieldGroups: FieldGroup[];
  fieldSelectionMap: FieldSelectionMap;
  setFieldSelectionMap: (map: FieldSelectionMap) => void;
}

const renderSelectList = (
  fieldGroups: FieldGroup[],
  fieldSelectionMap: FieldSelectionMap,
  setFieldSelectionMap: (map: FieldSelectionMap) => void,
  searchQuery: string,
  searchResult: Field[]
) => {
  if (searchQuery) {
    if (searchResult.length) {
      return (
        <List>
          {searchResult.map((field) => (
            <FieldOption
              key={field.fieldId}
              field={field}
              fieldSelectionMap={fieldSelectionMap}
              setFieldSelectionMap={setFieldSelectionMap}
            />
          ))}
        </List>
      );
    } else {
      return <p>No fields found</p>;
    }
  }

  if (
    !fieldGroups.length ||
    fieldGroups.every((group) => !group.fields.length)
  ) {
    return <p>No fields available</p>;
  }

  return (
    <List>
      {fieldGroups.map((group) => (
        <FieldGroupOption
          key={group.fieldGroupId}
          fieldGroup={group}
          fieldSelectionMap={fieldSelectionMap}
          setFieldSelectionMap={setFieldSelectionMap}
        />
      ))}
    </List>
  );
};

const FieldMultiSelect = (props: FieldMultiSelectProps) => {
  const { fieldGroups, fieldSelectionMap, setFieldSelectionMap } = props;
  const anchorEl = useRef<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const selectCount = useMemo(
    () =>
      Object.keys(fieldSelectionMap).filter((key) => fieldSelectionMap[key])
        .length,
    [fieldSelectionMap]
  );

  const handleClose = (event: React.ChangeEvent<{}>) => {
    // @ts-ignore
    if (anchorEl.current && anchorEl.current.contains(event.target)) {
      return;
    }

    setIsOpen(false);
  };

  const searchResult = useMemo(
    () => getUniqueFieldsFromGroupsBySearchQuery(fieldGroups, searchQuery),
    [fieldGroups, searchQuery]
  );

  return (
    <Container>
      {/* @ts-ignore */}
      <Button
        variant="outlined"
        buttonRef={anchorEl}
        aria-haspopup="true"
        aria-owns={isOpen ? "field-list" : null}
        onClick={() => setIsOpen((open) => !open)}
        disableRipple
      >
        <span>
          {selectCount ? `Selected ${selectCount} fields` : "Please select..."}
        </span>
        <Icon>{`arrow_${isOpen ? "drop_up" : "drop_down"}`}</Icon>
      </Button>
      <Popper
        open={isOpen}
        anchorEl={anchorEl.current}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          // @ts-ignore
          <Grow
            {...TransitionProps}
            id="field-list"
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <div>
                  <SearchBarContainer>
                    <SearchBar
                      height="38px"
                      value={searchQuery}
                      onChange={setSearchQuery}
                    />
                  </SearchBarContainer>
                  <ListContainer>
                    {renderSelectList(
                      fieldGroups,
                      fieldSelectionMap,
                      setFieldSelectionMap,
                      searchQuery,
                      searchResult
                    )}
                  </ListContainer>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Container>
  );
};

export default FieldMultiSelect;
